import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import api, { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import { useLockStore } from '@/app/store/lock'

type Phase = 'idle' | 'waiting' | 'denied' | 'expired' | 'failed'
interface Wrapped<T> { success: boolean; data: T }
/** A call made on the challenge token from the password step, not a session (lib/api/axios.ts). */
const onChallenge = (mfaToken: string) => ({ stepUpToken: mfaToken }) as Record<string, unknown>

/** How often the laptop asks whether the phone has answered. */
export const POLL_MS = 2000

/**
 * Sign-in, second step, on a device with no fingerprint reader: "approve on your phone"
 * (MFA build plan, Stage 5).
 *
 * This screen shows a two-digit number. The VaultSentinel app on the user's phone shows the
 * sign-in waiting and asks which number is on the screen; the phone's own key signs the answer.
 * The right number signs this device in. A wrong number, or "deny", ends the request - which is
 * the point: approving a prompt you did not start gets an attacker nothing.
 */
export function PhoneApproval() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [phase, setPhase] = useState<Phase>('idle')
    const [number, setNumber] = useState<number | null>(null)
    const [error, setError] = useState<string | null>(null)
    const timer = useRef<ReturnType<typeof setInterval> | null>(null)
    const stop = () => { if (timer.current) clearInterval(timer.current); timer.current = null }
    useEffect(() => stop, [])

    const start = async () => {
        const mfaToken = useAuthStore.getState().mfaToken
        if (!mfaToken) { setPhase('failed'); return }
        stop()
        setError(null)
        try {
            const asked = (await api.post<Wrapped<{ approvalId: number; matchNumber: number; expiresInSeconds: number }>>(
                '/auth/app-key/approval/begin', {}, onChallenge(mfaToken))).data.data
            setNumber(asked.matchNumber)
            setPhase('waiting')
            const deadline = Date.now() + asked.expiresInSeconds * 1000
            let asking = false
            timer.current = setInterval(async () => {
                if (asking) return
                if (Date.now() > deadline) { stop(); setPhase('expired'); return }
                asking = true
                try {
                    const answer = (await api.post<Wrapped<{ status: string; accessToken?: string; refreshToken?: string; user?: never; sessionId?: string }>>(
                        '/auth/app-key/approval/collect', { approvalId: asked.approvalId }, onChallenge(mfaToken))).data.data
                    if (answer.status === 'approved' && answer.accessToken && answer.refreshToken) {
                        stop()
                        const store = useAuthStore.getState()
                        // A fresh sign-in is not a locked session (see useLogin).
                        useLockStore.getState().unlock()
                        store.setTokens(answer.accessToken, answer.refreshToken, 15 * 60)
                        if (answer.user) store.setUser(answer.user)
                        if (answer.sessionId) store.setSessionId(answer.sessionId)
                        store.clearMfa()
                        navigate('/')
                    } else if (answer.status === 'denied' || answer.status === 'expired') {
                        stop()
                        setPhase(answer.status)
                    }
                } catch (e) {
                    stop()
                    setError(handleApiError(e))
                    setPhase('failed')
                } finally {
                    asking = false
                }
            }, POLL_MS)
        } catch (e) {
            setError(handleApiError(e))
            setPhase('failed')
        }
    }

    return (
        <div className="space-y-3 rounded-md border p-4" data-testid="phone-approval" data-phase={phase}>
            <div className="flex items-center gap-2 text-sm font-medium"><Smartphone className="h-4 w-4" />{t('phoneApproval.title')}</div>
            {phase === 'idle' && (
                <>
                    <p className="text-sm text-muted-foreground">{t('phoneApproval.description')}</p>
                    <Button type="button" className="w-full" onClick={start}>{t('phoneApproval.start')}</Button>
                </>
            )}
            {phase === 'waiting' && (
                <>
                    <p className="text-sm text-muted-foreground">{t('phoneApproval.waiting')}</p>
                    <p className="text-center font-mono text-5xl font-bold tracking-widest" data-testid="match-number" aria-live="polite">{number}</p>
                    <p className="text-xs text-muted-foreground">{t('phoneApproval.neverShare')}</p>
                </>
            )}
            {(phase === 'denied' || phase === 'expired' || phase === 'failed') && (
                <>
                    <p className="text-sm text-destructive" role="alert">{error ?? t(`phoneApproval.${phase}`)}</p>
                    <Button type="button" variant="outline" className="w-full" onClick={start}>{t('phoneApproval.tryAgain')}</Button>
                </>
            )}
        </div>
    )
}
